import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DocumentConstants, DocumentExtensionHandling } from 'app/constants/document.constants';
import { FormService } from 'app/services/form.service';
import { AlertsService } from 'app/ui-components/alerts/alerts.service';
import { AlertMessagesConfig } from 'config/alert-messages.config';
import { catchError, map, Observable, throwError } from 'rxjs';

export interface DialogData {
  fileName: string;
  allowedFileTypes: string[];
  fileAcceptTypes: string[];
  paymentAmount: string | undefined;
  formType?: string;
  sysId?: string;
  caseSysId?: string;
}

@Component({
  selector: 'app-upload-file-dialog',
  templateUrl: './upload-file-dialog.component.html',
  styleUrls: ['./upload-file-dialog.component.scss']
})
export class UploadFileDialogComponent {
  readonly dialogRef = inject(MatDialogRef<UploadFileDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private _snackBar = inject(MatSnackBar);
  private fileName = this.data?.fileName ? this.data.fileName : undefined;
  private allowedFileTypes = this.data?.allowedFileTypes ? this.data.allowedFileTypes : undefined;
  private fileAcceptTypes = this.data?.fileAcceptTypes ? this.data.fileAcceptTypes : [];
  private paymentAmount = this.data?.paymentAmount? this.data.paymentAmount : '0.00';
  private formType = this.data?.formType ? this.data.formType : undefined;
  private sysId = this.data?.sysId ? this.data.sysId : undefined;
  private caseSysId = this.data?.caseSysId ? this.data.caseSysId : undefined;

  constructor(private formService: FormService, public alertsMessageConfig: AlertMessagesConfig, private alertService: AlertsService) {}

  public uploadDocument(doc: File) {
    if(!this.fileName) {
      this.fileName = doc.name;
    }

    if(!DocumentExtensionHandling.isFileTypeAllowedForUpload(doc.name, this.allowedFileTypes)) { 
      this.alertService.displayError('Invalid file type.');
      this.dialogRef.close(doc);
      return;
    }

    if(!this.caseSysId) {
      this.caseSysId = this.formService.getCurrentUsanFormValue().get('caseSysId')?.value;
    }
    if(!this.caseSysId) {
      this.createCase().subscribe({
        next: (response) => { 
          this.upload(doc, this.formType, this.caseSysId);
        },
        error: (err) => {
          this.openUploadErrorSnackBar(err)
          console.error(err);
          this.dialogRef.close();
          throw new Error(`There was an error while attempting to upload the document.`);
        }
    });
    } else {
      this.upload(doc, this.formType, this.caseSysId);
    }
    this.dialogRef.close(doc);
  }

  private upload(doc: File, formType?: string, caseSysId?: string) {
    if(!this.fileName) {
      throw new Error(`There was an error while attempting to upload the document.`);
    }

    this.formService.uploadFileAndRefreshAttachments(doc, this.fileName, formType, caseSysId).subscribe({
      next: (response) => {
         // Making sure data is updated on the form on proof of payment upload.
        if(this.fileName === DocumentConstants.PROOF_OF_PAYMENT) {
          this.formService.updateWireTransferDataOnUploadProofOfPayment(this.amountAsCurrencyString);
        }
        this.openUploadSuccessSnackBar();
      },
      error: (err) => {
        this.openUploadErrorSnackBar(err)
        console.error(err);
        this.dialogRef.close();
        throw new Error(`There was an error while attempting to upload the document.`);
      }
    });
  }

  private createCase(): Observable<any>  {
    return this.formService.createOrSaveCase().pipe(
        map(response => {
          this.sysId = response.sys_Id;
          this.caseSysId = response.caseSysId;
        }),
        catchError(error => {
          console.error(error);
          return throwError(() => new Error(this.alertsMessageConfig.defaultServerError));
        })
      );
  }

  get amountAsCurrencyString(): string {
    if(!this.paymentAmount) {
      return '0.00';
    }

    // Convert to a float first to allow only 2 decimal places during the conversion.
    const asFloat = parseFloat(this.paymentAmount);
    return parseFloat(asFloat.toFixed(2)).toString();
  } 

  openUploadSuccessSnackBar() {
    this._snackBar.open("File uploaded successfully.", "Close", { duration: 3000 });
  }

  openUploadErrorSnackBar(error) {
    this._snackBar.open("There was an error while attempting to upload the file.", "Close", { duration: 3000 });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  get acceptedTypes(): string[] {
    if(this.fileAcceptTypes === undefined || this.fileAcceptTypes.length < 1) {
      return DocumentExtensionHandling.allAcceptableUploadTypes();
    }
    
    return this.fileAcceptTypes;
  }
}
